import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import { Layout } from "./components/Layout";

// Pages
import Home from "./pages/Home";
import Product from "./pages/Product";
import HowItWorks from "./pages/HowItWorks";
import Pricing from "./pages/Pricing";
import Services from "./pages/Services";
import Benchmarks from "./pages/Benchmarks";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Legal from "./pages/Legal";
import NotFound from "./pages/NotFound";

import "@/App.css";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

function CarlinhosCommentary() {
  const { pathname } = useLocation();
  const { language } = useLanguage();

  const [visible, setVisible]   = useState(false);
  const [message, setMessage]   = useState("");
  const [mounted, setMounted]   = useState(false);
  const [side, setSide]         = useState("right");
  const [yPos, setYPos]         = useState(300);
  const [isDvd, setIsDvd]       = useState(false);
  const [dvdPos, setDvdPos]     = useState({ x: 200, y: 300 });
  const [fleeMsg, setFleeMsg]   = useState(null);

  const dvdVelRef     = useRef({ vx: 3, vy: 2 });
  const dvdRafRef     = useRef(null);
  const isDvdRef      = useRef(false);
  const idleTimerRef  = useRef(null);
  const sideRef       = useRef("right");
  const yPosRef       = useRef(300);
  const visibleRef    = useRef(false);
  const fleeActiveRef = useRef(false);
  const fleeTimerRef  = useRef(null);

  const isContactRoute = /\/contact\/?$/.test(pathname);
  const IMG_W      = 130;
  const IDLE_MS    = 90000;
  const FLEE_DIST  = 150;
  const BUBBLE_W   = 260;

  const routeMessages = useMemo(() => ({
    pt: {
      home: [
        "Prometo não atravessar a tela. Hoje estou só espionando os cantos.",
        "Apareci aqui porque alguém tinha que fazer isso.",
        "Não sou pop-up. Sou diferente. Sou o Carlinhos.",
        "Se você rolar até o fim, eu apareço de novo. É minha vida.",
      ],
      product: [
        "Expliquei isso pra minha avó. Ela automatizou a loja dela.",
        "Parece muita coisa até você entender. Aí parece pouco.",
        "Sim, eu leio a documentação. Alguém tem que ler.",
        "Engrenagem travada é o motivo de eu existir.",
      ],
      services: [
        "Copiar e colar dado manualmente é crime. Não juridicamente, mas deveria ser.",
        "Tem gente que faz isso na mão até hoje. Eu choro por eles.",
        "Integração boa é aquela que você esquece que existe. Igual a mim, mas ao contrário.",
      ],
      pricing: [
        "Sem mensalidade travando seu caixa. Eu não cobro nada também, só apareço.",
        "Success fee significa que a gente só ganha se você ganhar. Não inventamos isso, mas adotamos.",
        "Risco zero foi ideia minha. Pode me agradecer.",
      ],
      benchmarks: [
        "Esses números são reais. Fui eu que espiei.",
        "Benchmark sem contexto é só enfeite de apresentação.",
        "Coloquei esses dados aqui e fui embora. Voltei pra confirmar.",
      ],
      about: [
        "Sim, sou mascote. Não, não tenho complexo de inferioridade.",
        "A equipe é séria. Eu sou o lado divertido do CNPJ.",
        "Quebrar a quarta parede foi ideia minha também.",
        "Todo mundo tem um Carlinhos na empresa. Aqui o meu nome é literal.",
      ],
      blog: [
        "Li antes de você. Tá bom. Pode ler.",
        "Vim fiscalizar o conteúdo. Aprovado.",
        "Artigo técnico sem enrolação é raro. Aqui tem.",
        "Se você abriu esse blog às 23h, eu te entendo.",
      ],
      legal: [
        "Contrato claro é sinal de empresa séria. E de advogado bom.",
        "Li os termos. Você não vai ler, mas eu li.",
        "Transparência não é diferencial. É o mínimo. Mas vale mencionar.",
      ],
    },
    en: {
      home: [
        "I promised not to cross the screen. Today I'm just spying from the corners.",
        "I showed up because someone had to.",
        "I'm not a pop-up. I'm different. I'm Carlinhos.",
        "If you scroll to the bottom, I'll show up again. That's my life.",
      ],
      product: [
        "I explained this to my grandma. She automated her store.",
        "Looks like a lot until you get it. Then it looks like nothing.",
        "Yes, I read the documentation. Someone has to.",
        "A stuck gear is the reason I exist.",
      ],
      services: [
        "Copy-pasting data by hand is a crime. Not legally, but it should be.",
        "Some people still do this manually. I cry for them.",
        "A good integration is one you forget exists. Like me, but the opposite.",
      ],
      pricing: [
        "No fixed monthly fee locking up your cash. I don't charge anything either, I just show up.",
        "Success fee means we only win if you win. We didn't invent it, but we stand by it.",
        "Zero risk was my idea. You're welcome.",
      ],
      benchmarks: [
        "These numbers are real. I personally spied on them.",
        "Bad benchmarks are just pretty PowerPoints. Not here.",
        "I put these numbers up and left. Came back to double-check.",
      ],
      about: [
        "Yes, I'm a mascot. No, I don't have an inferiority complex.",
        "The team is serious. I'm the fun side of the company.",
        "Breaking the fourth wall was my idea too.",
        "Every company has a Carlinhos. Here, mine is literal.",
      ],
      blog: [
        "Read it before you. It's good. Go ahead.",
        "Came to inspect the content. Approved.",
        "Technical content without filler is rare. You'll find it here.",
        "If you opened this blog at 11pm, I get it.",
      ],
      legal: [
        "A clear contract is the sign of a serious company. And a good lawyer.",
        "I read the terms. You won't, but I did.",
        "Transparency isn't a differentiator. It's the minimum. But worth mentioning.",
      ],
    },
  }), []);

  const getRouteKey = useCallback(() => {
    if (pathname.includes("/services"))   return "services";
    if (pathname.includes("/pricing"))    return "pricing";
    if (pathname.includes("/benchmarks")) return "benchmarks";
    if (pathname.includes("/about"))      return "about";
    if (pathname.includes("/blog"))       return "blog";
    if (
      pathname.includes("/terms") ||
      pathname.includes("/privacy") ||
      pathname.includes("/commercial-terms")
    ) return "legal";
    if (pathname.includes("/product") || pathname.includes("/how-it-works")) return "product";
    return "home";
  }, [pathname]);

  // Altura aleatória — evita topo (navbar) e fundo (WhatsApp)
  const getRandomY = useCallback(() => {
    const h = window.innerHeight;
    const UNIT_H = IMG_W + 140;
    const minY = 80;
    const maxY = h - UNIT_H - 80;
    return minY + Math.random() * Math.max(0, maxY - minY);
  }, []);

  // Reset inatividade
  const resetIdle = useCallback(() => {
    if (isDvdRef.current) {
      isDvdRef.current = false;
      setIsDvd(false);
      if (dvdRafRef.current) cancelAnimationFrame(dvdRafRef.current);
    }
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      isDvdRef.current = true;
      setIsDvd(true);
    }, IDLE_MS);
  }, []);

  useEffect(() => {
    setMounted(true);
    idleTimerRef.current = setTimeout(() => {
      isDvdRef.current = true;
      setIsDvd(true);
    }, IDLE_MS);
    const events = ["mousemove", "keydown", "scroll", "click", "touchstart"];
    events.forEach(e => document.addEventListener(e, resetIdle, { passive: true }));
    return () => {
      events.forEach(e => document.removeEventListener(e, resetIdle));
      clearTimeout(idleTimerRef.current);
    };
  }, [resetIdle]);

  // DVD screensaver
  useEffect(() => {
    if (!isDvd) {
      if (dvdRafRef.current) cancelAnimationFrame(dvdRafRef.current);
      return;
    }
    let pos = { x: window.innerWidth - IMG_W - 24, y: yPosRef.current };
    const vel = dvdVelRef.current;
    const animate = () => {
      pos.x += vel.vx;
      pos.y += vel.vy;
      const maxX = window.innerWidth  - IMG_W;
      const maxY = window.innerHeight - IMG_W;
      if (pos.x <= 0)    { vel.vx =  Math.abs(vel.vx); pos.x = 0; }
      if (pos.x >= maxX) { vel.vx = -Math.abs(vel.vx); pos.x = maxX; }
      if (pos.y <= 0)    { vel.vy =  Math.abs(vel.vy); pos.y = 0; }
      if (pos.y >= maxY) { vel.vy = -Math.abs(vel.vy); pos.y = maxY; }
      setDvdPos({ x: pos.x, y: pos.y });
      dvdRafRef.current = requestAnimationFrame(animate);
    };
    dvdRafRef.current = requestAnimationFrame(animate);
    return () => {
      if (dvdRafRef.current) cancelAnimationFrame(dvdRafRef.current);
    };
  }, [isDvd]);

  // Fuga do mouse
  useEffect(() => {
    if (isContactRoute) return;
    const onMouseMove = (e) => {
      if (isDvdRef.current || fleeActiveRef.current || !visibleRef.current) return;
      const unitX = sideRef.current === "right"
        ? window.innerWidth - BUBBLE_W - 8
        : 8;
      const unitCX = unitX + BUBBLE_W / 2;
      const unitCY = yPosRef.current + IMG_W / 2;
      const dist = Math.hypot(e.clientX - unitCX, e.clientY - unitCY);
      if (dist < FLEE_DIST) {
        fleeActiveRef.current = true;
        setFleeMsg({ x: unitCX - 30, y: unitCY - 20 });
        const newSide = sideRef.current === "right" ? "left" : "right";
        const newY    = getRandomY();
        sideRef.current = newSide;
        yPosRef.current = newY;
        setSide(newSide);
        setYPos(newY);
        clearTimeout(fleeTimerRef.current);
        fleeTimerRef.current = setTimeout(() => {
          setFleeMsg(null);
          setTimeout(() => { fleeActiveRef.current = false; }, 600);
        }, 1200);
      }
    };
    window.addEventListener("mousemove", onMouseMove);
    return () => window.removeEventListener("mousemove", onMouseMove);
  }, [isContactRoute, getRandomY]);

  // Ciclo de mensagens
  useEffect(() => {
    if (isContactRoute) {
      setVisible(false);
      visibleRef.current = false;
      return;
    }
    let hideTimeout, showTimeout, firstTimeout, intervalId;

    const showMoment = () => {
      if (isDvdRef.current) return;
      const routeKey = getRouteKey();
      const messages = routeMessages[language]?.[routeKey] || routeMessages[language]?.home || [];
      setMessage(messages[Math.floor(Math.random() * messages.length)] || "");

      const newSide = Math.random() < 0.5 ? "left" : "right";
      const newY    = getRandomY();
      sideRef.current = newSide;
      yPosRef.current = newY;
      setSide(newSide);
      setYPos(newY);

      visibleRef.current = false;
      setVisible(false);
      showTimeout = setTimeout(() => { visibleRef.current = true;  setVisible(true);  }, 50);
      hideTimeout = setTimeout(() => { visibleRef.current = false; setVisible(false); }, 5600);
    };

    firstTimeout = setTimeout(showMoment, 1400);
    intervalId   = setInterval(showMoment, 17000);
    return () => {
      clearInterval(intervalId);
      clearTimeout(firstTimeout);
      clearTimeout(showTimeout);
      clearTimeout(hideTimeout);
    };
  }, [isContactRoute, language, pathname, routeMessages, getRouteKey, getRandomY]);

  if (!mounted || isContactRoute) return null;

  const slideHidden = side === "right"
    ? `translateX(${BUBBLE_W + 20}px)`
    : `translateX(-${BUBBLE_W + 20}px)`;

  const unitStyle = {
    position:      "fixed",
    top:           yPos,
    zIndex:        9999,
    display:       "flex",
    flexDirection: "column",
    alignItems:    "center",
    pointerEvents: "none",
    transition:    "transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)",
    transform:     visible ? "translateX(0)" : slideHidden,
    ...(side === "right" ? { right: 0 } : { left: 0 }),
  };

  return createPortal(
    <div aria-hidden="true">
      <style>{`
        .carlinhos-root { display: none; }
        @media (min-width: 1280px) { .carlinhos-root { display: block; } }
      `}</style>

      <div className="carlinhos-root">

        {/* Unidade principal: Carlinhos em cima do balão */}
        {!isDvd && (
          <div style={unitStyle}>
            <img
              src="/carlinhos-peek.png"
              alt="Carlinhos"
              style={{
                width: IMG_W,
                display: "block",
                marginBottom: -8,
                position: "relative",
                zIndex: 1,
              }}
              className="object-contain"
            />
            <div
              style={{ width: BUBBLE_W }}
              className="rounded-2xl border border-slate-700/80 bg-slate-950/95 px-5 py-4 text-sm font-medium leading-relaxed text-slate-100 shadow-2xl backdrop-blur"
            >
              {message}
            </div>
          </div>
        )}

        {/* "olééé" ao fugir */}
        {fleeMsg && (
          <div
            style={{
              position:      "fixed",
              left:          fleeMsg.x,
              top:           fleeMsg.y,
              zIndex:        10001,
              pointerEvents: "none",
            }}
            className="text-2xl font-bold text-white drop-shadow-lg animate-bounce select-none"
          >
            olééé
          </div>
        )}

        {/* Modo DVD */}
        {isDvd && (
          <div
            style={{
              position:      "fixed",
              left:          dvdPos.x,
              top:           dvdPos.y,
              zIndex:        9999,
              pointerEvents: "none",
            }}
          >
            <img
              src="/carlinhos-peek.png"
              alt="Carlinhos"
              style={{ width: IMG_W }}
              className="object-contain"
            />
          </div>
        )}

      </div>
    </div>,
    document.body
  );
}

function AppContent() {
  const { pathname } = useLocation();
  const isContactRoute = /\/contact\/?$/.test(pathname);

  return (
    <>
      <ScrollToTop />
      {!isContactRoute && <CarlinhosCommentary />}
      <Routes>
        <Route path="/" element={<Navigate to="/pt" replace />} />

        <Route path="/pt" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="product" element={<Product />} />
          <Route path="how-it-works" element={<HowItWorks />} />
          <Route path="pricing" element={<Pricing />} />
          <Route path="services" element={<Services />} />
          <Route path="benchmarks" element={<Benchmarks />} />
          <Route path="about" element={<About />} />
          <Route path="contact" element={<Contact />} />
          <Route path="blog" element={<Blog />} />
          <Route path="blog/:slug" element={<BlogPost />} />
          <Route path="terms" element={<Legal />} />
          <Route path="privacy" element={<Legal />} />
          <Route path="commercial-terms" element={<Legal />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        <Route path="/en" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="product" element={<Product />} />
          <Route path="how-it-works" element={<HowItWorks />} />
          <Route path="pricing" element={<Pricing />} />
          <Route path="services" element={<Services />} />
          <Route path="benchmarks" element={<Benchmarks />} />
          <Route path="about" element={<About />} />
          <Route path="contact" element={<Contact />} />
          <Route path="blog" element={<Blog />} />
          <Route path="blog/:slug" element={<BlogPost />} />
          <Route path="terms" element={<Legal />} />
          <Route path="privacy" element={<Legal />} />
          <Route path="commercial-terms" element={<Legal />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        <Route path="*" element={<Navigate to="/pt" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AppContent />
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
